/**
 * Excel Export Service
 * Handles template-based Excel exports for all entity types
 */

import httpClient from './httpClient';

class ExcelExportService {
  constructor() {
    this.baseURL = '/api/excel-export';
  }

  /**
   * Export using template - The primary export method
   * @param {string} entityType - Entity type ('sales', 'purchase', 'quotation')
   * @param {Object} payload - Export payload
   * @param {string} payload.template_id - Template identifier
   * @param {Array<string>} payload.sales_order_ids - For sales orders
   * @param {Array<string>} payload.purchase_order_ids - For purchase orders
   * @param {Array<string>} payload.quotation_ids - For quotations
   * @param {Object} payload.filters - Optional filters
   * @param {Function} payload.onProgress - Optional progress callback
   * @returns {Promise<Object>} File blob and filename
   */
  async exportUsingTemplate(entityType, payload) {
    try {
      // Validate entity type
      const validTypes = ['sales', 'purchase', 'quotation', 'warehouse_inbound', 'warehouse_outbound'];
      if (!validTypes.includes(entityType)) {
        throw new Error(`Invalid entity type: ${entityType}. Must be one of: ${validTypes.join(', ')}`);
      }

      // Map entity type to correct endpoint
      const endpointMap = {
        'sales': 'sales-orders',
        'purchase': 'purchase-orders',
        'quotation': 'quotations',
        'warehouse_inbound': 'warehouse-movements/inbound',
        'warehouse_outbound': 'warehouse-movements/outbound'
      };

      const endpoint = endpointMap[entityType];

      // Ensure template_id is provided
      if (!payload.template_id) {
        throw new Error('template_id is required for template-based export');
      }

      // Extract progress callback if provided
      const { onProgress, ...requestPayload } = payload;

      const response = await httpClient.post(`${this.baseURL}/${endpoint}/template`, requestPayload, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 60000, // 60 second timeout
        onDownloadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percentCompleted);
          }
        },
      });

      return {
        blob: response.data,
        filename: this._extractFilename(response),
      };
    } catch (error) {
      console.error(`Export ${entityType} with template error:`, error);
      throw this._handleExportError(error);
    }
  }

  /**
   * Download the exported file blob
   * @param {Blob} blob - File blob
   * @param {string} filename - Desired filename
   */
  downloadBlob(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || this._generateDefaultFilename();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Extract filename from response headers
   * @private
   */
  _extractFilename(response) {
    const contentDisposition = response.headers['content-disposition'];
    if (!contentDisposition) {
      return this._generateDefaultFilename();
    }

    // Try to extract filename from Content-Disposition header
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      let filename = filenameMatch[1].replace(/['"]/g, '');
      // Handle UTF-8 encoded filenames
      if (contentDisposition.includes('filename*=')) {
        const utf8Match = contentDisposition.match(/filename\*=UTF-8''(.*?)(?:;|$)/);
        if (utf8Match && utf8Match[1]) {
          try {
            filename = decodeURIComponent(utf8Match[1]);
          } catch (e) {
            console.warn('Failed to decode UTF-8 filename:', e);
          }
        }
      }
      return filename;
    }

    return this._generateDefaultFilename();
  }

  /**
   * Generate default filename with timestamp
   * @private
   */
  _generateDefaultFilename() {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
    return `Export_${timestamp}.xlsx`;
  }

  /**
   * Handle export errors
   * @private
   */
  _handleExportError(error) {
    if (error.response) {
      // Server responded with error
      if (error.response.data instanceof Blob) {
        // Try to parse error from blob
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const errorData = JSON.parse(reader.result);
              resolve(new Error(errorData.error || errorData.message || 'Export failed'));
            } catch (e) {
              resolve(new Error('Export failed with unknown error'));
            }
          };
          reader.onerror = () => {
            resolve(new Error('Export failed - unable to read error response'));
          };
          reader.readAsText(error.response.data);
        });
      }

      // Regular error response
      const message = error.response.data?.error ||
                     error.response.data?.message ||
                     `Export failed with status ${error.response.status}`;
      return new Error(message);
    }

    if (error.code === 'ECONNABORTED') {
      return new Error('Export timed out - the file may be too large');
    }

    if (error.message) {
      return error;
    }

    return new Error('Export failed - please try again');
  }
}

// Create and export singleton instance
const excelExportService = new ExcelExportService();
export default excelExportService;